import { useStripeBillingEnabled } from '@/composables/useStripeBillingEnabled'
import type { BillingSummary } from '@/model/Billing'
import { billingService } from '@/services/BillingService'
import { ServiceError } from '@/services/util/ServiceError'
import {
  checkoutTrialWarningParamsFromSummary,
  resolveCheckoutTrialWarningDaysRemaining,
  shouldShowCheckoutTrialWarning
} from '@/utils/billingTrialUtils'
import { computed, ref, watch } from 'vue'

export function useCheckoutTrialWarning() {
  const { companyId, stripeBillingEnabled } = useStripeBillingEnabled()
  const summary = ref<BillingSummary | undefined>()

  async function loadSummary(): Promise<void> {
    if (!stripeBillingEnabled.value || !companyId.value) {
      summary.value = undefined
      return
    }
    const response = await billingService.getSummary()
    if (response instanceof ServiceError) {
      summary.value = undefined
      return
    }
    summary.value = response
  }

  watch(
    [companyId, stripeBillingEnabled],
    () => {
      void loadSummary()
    },
    { immediate: true }
  )

  const warningParams = computed(() =>
    summary.value
      ? checkoutTrialWarningParamsFromSummary(summary.value, stripeBillingEnabled.value)
      : null
  )

  const daysRemaining = computed(() => {
    if (!warningParams.value) {
      return null
    }
    return resolveCheckoutTrialWarningDaysRemaining(warningParams.value)
  })

  const showWarning = computed(() => {
    if (!warningParams.value) {
      return false
    }
    return shouldShowCheckoutTrialWarning(warningParams.value)
  })

  return {
    showWarning,
    daysRemaining
  }
}
